import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { AbsencesService } from '../services/absencesService';
import { MockAbsencesService } from '../services/mockAbsencesService';
import { GlobalMockupService } from '@/common/services/mockup';
import { logger } from '@/common/services/config';
import type { AbsenceModel } from '../types/absencesTypes';

export const useAbsencesStore = defineStore('absences', () => {
  // Constants
  const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

  // State
  const absencesData = ref<AbsenceModel[] | null>(null);
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const lastFetchedAt = ref<number | null>(null);

  // Getters
  const isLoaded = computed(() => lastFetchedAt.value !== null);

  const isCacheExpired = computed(() => {
    if (!lastFetchedAt.value) return true;
    return Date.now() - lastFetchedAt.value > CACHE_TTL_MS;
  });
  const sortedAbsences = (absences: AbsenceModel[]) => {
    return [...absences].sort((a, b) => {
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    });
  };

  // Actions
  const fetchAbsences = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      let response: AbsenceModel[];

      if (GlobalMockupService.isFeatureMockupEnabled('absences')) {
        response = await MockAbsencesService.getAbsences();
      } else {
        response = await AbsencesService.getAbsences();
      }

      absencesData.value = response;
      lastFetchedAt.value = Date.now();
    } catch (err) {
      logger.error('Error fetching absences:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const initializeAbsences = async () => {
    if (!isLoaded.value || isCacheExpired.value) {
      await fetchAbsences();
    }
  };

  const forceReloadAbsences = async () => {
    lastFetchedAt.value = null;
    absencesData.value = null;
    error.value = null;

    await fetchAbsences();
  };

  const reset = () => {
    absencesData.value = null;
    isLoading.value = false;
    error.value = null;
    lastFetchedAt.value = null;
  };

  return {
    // State
    absencesData,
    isLoading,
    error,
    lastFetchedAt,

    // Getters
    isLoaded,
    isCacheExpired,

    // Actions
    sortedAbsences,
    fetchAbsences,
    initializeAbsences,
    forceReloadAbsences,
    reset,
  };
});
